// История списаний, пакеты, подписка и оплата. Экраны 09 и 10.
//
// Четыре ручки, а не одна: они отвечают на разные вопросы и меняются с разной
// скоростью. Журнал — это прошлое, пакеты и подписка — настоящее, чекаут —
// действие. Свести их в один ответ значило бы перезапрашивать историю ради
// того, чтобы узнать цену пакета.

import type { LedgerEvent, PackOffer, TariffId, UnitId } from '@smyslika/core'

import type { Client } from './client.ts'

/**
 * Живой пакет — купленный или начисленный поддержкой (`source`): сколько
 * осталось и, если срок есть, до какого числа.
 */
export interface ActivePack {
  id: string
  unit: UnitId
  amount: number
  remaining: number
  /** Пусто — не сгорает. Так живут все три пакета каталога с 06.09.2026. */
  expiresAt?: string
  source: string
}

export interface PackOfferView extends PackOffer {
  /** До какого числа не сгорит, если купить сейчас. Пусто — не сгорает вовсе. */
  expiresIfBoughtNow?: string
  /** Готовая строка про срок: экрану не приходится решать это самому. */
  note: string
}

export interface Subscription {
  id: string
  tariff: TariffId
  priceRub: number
  autoRenew: boolean
  nextChargeAt?: string
  cancelledAt?: string
  cancelReason?: string
  cancelLabel?: string
}

/** Сохранённые реквизиты. От них можно отказаться электронно — с 01.03.2026. */
export interface PaymentMethod {
  id: string
  title: string
  savedAt: string
}

export interface Checkout {
  invoiceId: number
  amountRub: number
  /** Куда уводить человека платить. Страница эквайринга, не наша. */
  url: string
}

/**
 * Одна строка истории оплат. Экран 10 (веб W08) показывает их списком.
 *
 * `status` здесь честный и включает неуспешные: счёт, который не оплатился,
 * человек всё равно видел на странице ЮKassa, и не найдя его у нас, решит,
 * что деньги ушли в никуда. Отказ, названный отказом, снимает этот вопрос
 * без обращения в поддержку.
 *
 * `refunded` — вернули всё, и в базе такой статус не пишется: возвращённый
 * платёж хранится `paid`, потому что по этому статусу `settle` узнаёт уже
 * выданный. Переводит сервер, отдавая историю, — сравнивать `refundedRub` с
 * `amountRub` экрану не нужно.
 */
export interface PaymentRow {
  invoiceId: number
  kind: 'subscription' | 'renewal' | 'pack'
  status: 'pending' | 'paid' | 'failed' | 'refunded'
  amountRub: number
  tariff?: TariffId
  unit?: UnitId
  packAmount?: number
  paidAt?: string
  /**
   * Всего возвращено. Есть только у оплаченного и только ненулевое: при
   * полном возврате статус `refunded`, при частичном остаётся `paid`.
   */
  refundedRub?: number
  failureReason?: string
  createdAt: string
}

export const createBilling = (client: Client) => ({
  /** История оплат: подписки, продления, пакеты — со статусами. */
  payments: () => client.request<{ payments: PaymentRow[] }>('/v1/payments'),

  ledger: (limit?: number) =>
    client.request<{ events: LedgerEvent[] }>(`/v1/ledger${limit != null ? `?limit=${limit}` : ''}`),

  packs: () => client.request<{ offers: PackOfferView[]; active: ActivePack[] }>('/v1/packs'),

  subscription: () =>
    client.request<{ subscriptions: Subscription[]; methods: PaymentMethod[] }>('/v1/subscription'),

  /** Отмена автопродления. Доступ остаётся до конца оплаченного периода. */
  cancelRenewal: (id: string) =>
    client.request<{ cancelled: true; accessUntil?: string; message: string }>(
      `/v1/subscription/${encodeURIComponent(id)}/renewal`,
      { method: 'DELETE' },
    ),

  /** Электронный отказ от сохранённых реквизитов. Списания прекращаются. */
  revokeMethod: (id: string) =>
    client.request<{ revoked: true; cancelledSubscriptions: number; message: string }>(
      `/v1/payment-methods/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),

  buyTariff: (tariff: TariffId) =>
    client.request<Checkout>('/v1/checkout', { method: 'POST', body: { kind: 'subscription', tariff } }),

  buyPack: (unit: UnitId) =>
    client.request<Checkout>('/v1/checkout', { method: 'POST', body: { kind: 'pack', unit } }),
})

export type BillingApi = ReturnType<typeof createBilling>
